// src/pages/StaffEditPage.jsx
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { api, staffApi } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import Breadcrumb from "../components/Breadcrumb";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import CustomSelect from "../components/common/CustomSelect";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { generateSecurePassword } from "../utils/generatePassword";

const validationSchema = Yup.object().shape({
  staff_name: Yup.string().required("Name is required"),
  staff_email: Yup.string()
    .required("Email is required")
    .email("Invalid email"),
  staff_phone_no: Yup.string()
    .required("Phone number is required")
    .test("is-valid-phone", "Invalid phone number", (value) =>
      isValidPhoneNumber(value || "")
    ),
  client_role_id: Yup.object().required("Role is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  confirm_password: Yup.string().when("password", {
    is: (val) => val && val.length > 0,
    then: (schema) =>
      schema
        .required("Confirm password is required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const StaffEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = location.state?.staffData?.id;

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [clientRoles, setClientRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      staff_name: "",
      staff_email: "",
      staff_phone_no: "",
      client_role_id: null,
      password: "",
      confirm_password: "",
    },
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [countriesRes, staffRes, rolesRes] = await Promise.all([
          fetch("https://restcountries.com/v3.1/all?fields=name,idd,flag").then(
            (r) => r.json()
          ),
          staffApi.getStaffById(staffId),
          api.get("/get-all-client-roles?want_pagination=0"),
        ]);

        const countries = countriesRes
          .map((country) => ({
            value: country.idd.root + (country.idd.suffixes?.[0] || ""),
            label: `${country.flag} ${country.name.common} (${
              country.idd.root + (country.idd.suffixes?.[0] || "")
            })`,
          }))
          .filter((c) => c.value)
          .sort((a, b) => a.label.localeCompare(b.label));

        const staff = staffRes.data;
        const roleOptions =
          rolesRes.data?.data?.map((r) => ({
            label: r.role_name,
            value: r.client_role_id,
          })) || [];

        const phoneMatch = staff.staff_phone?.match(/^(\+\d{1,4})(\d{5,15})$/);
        const phoneNumberFull = staff.staff_phone || "";

        reset({
          staff_name: staff.staff_name,
          staff_email: staff.staff_email,
          staff_phone_no: phoneNumberFull,
          client_role_id: {
            value: staff.client_role_id,
            label: staff.role_name || "Unknown Role",
          },
          password: "",
          confirm_password: "",
        });

        setCountryOptions(countries);
        setClientRoles(roleOptions);
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [staffId, reset]);

  const generatePassword = () => {
    const password = generateSecurePassword();
    setValue("password", password);
    setValue("confirm_password", password);
    setShowPassword(true);
    setShowConfirmPassword(true);
    handleCustomResponse("info", "Password generated!");
  };

  const onSubmit = async (data) => {
    setIsSubmitLoading(true);
    try {
      const payload = {
        staff_name: data.staff_name,
        staff_email: data.staff_email,
        staff_phone_no: data.staff_phone_no,
        client_role_id: data.client_role_id.value,
      };

      if (data.password) payload.password = data.password;

      const res = await api.put(`/update-staff/${staffId}`, payload);
      handleApiResponse(res.data, "Staff updated", "PUT");

      navigate("/staff");
    } catch (err) {
      const error = err.response?.data || err.message;

      handleApiResponse(error, "Staff updated Failed", "PUT");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading)
    return (
      <>
        <Breadcrumb title="Edit Staff" />
        <LoadingComponent />
      </>
    );

  return (
    <>
      <Breadcrumb title="Edit Staff" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row g-20">
              {/* Full Name */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  {...register("staff_name")}
                  className="form-control radius-8"
                  placeholder="Enter Full Name"
                />
                {errors.staff_name && (
                  <span className="text-danger-600">
                    {errors.staff_name.message}
                  </span>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  {...register("staff_email")}
                  className="form-control radius-8"
                  placeholder="Enter Email Address"
                />
                {errors.staff_email && (
                  <span className="text-danger-600">
                    {errors.staff_email.message}
                  </span>
                )}
              </div>

              {/* Phone */}
              {/* <div className="col-md-6 mb-20">
                <label className="form-label">Phone *</label>
                <Controller
                  name="staff_phone_no"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      international
                      defaultCountry="PK"
                      className={`form-control radius-8 ${
                        errors.staff_phone_no ? "is-invalid" : ""
                      }`}
                      style={{ width: "100%" }}
                    />
                  )}
                />
                {errors.staff_phone_no && (
                  <span className="text-danger-600">
                    {errors.staff_phone_no.message}
                  </span>
                )}
              </div> */}
              <div className="col-md-6 mb-20">
                <CustomPhoneInput
                  name="staff_phone_no"
                  control={control}
                  errors={errors}
                />
              </div>

              {/* Role */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Role *</label>
                <Controller
                  name="client_role_id"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="client_role_id"
                      value={field.value}
                      onChange={field.onChange}
                      options={clientRoles}
                      placeholder="Select Role"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No roles available</p>
                          <Link
                            to="/staff-role-create"
                            className="text-primary-600"
                          >
                            + Create New Role
                          </Link>
                        </div>
                      )}
                      addNewLabel="Add New Role"
                      addNewRoute="/staff-role-create"
                    />
                  )}
                />
                {errors.client_role_id && (
                  <span className="text-danger-600">
                    {errors.client_role_id.message}
                  </span>
                )}
              </div>

              {/* Password */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className="form-control radius-8"
                    style={{ paddingRight: "4rem" }}
                    placeholder="Enter Password"
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "3rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={generatePassword}
                  >
                    <Icon icon="carbon:password" />
                  </span>
                  <span
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon
                      icon={showPassword ? "ri:eye-off-line" : "ri:eye-line"}
                    />
                  </span>
                </div>
                {errors.password && (
                  <span className="text-danger-600">
                    {errors.password.message}
                  </span>
                )}
              </div>

              {/* Confirm Password */}
              <div className="col-md-6 mb-20">
                <label className="form-label">Confirm Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    {...register("confirm_password")}
                    className="form-control radius-8"
                    style={{ paddingRight: "2.5rem" }}
                    placeholder="Confirm Password"
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Icon
                      icon={
                        showConfirmPassword ? "ri:eye-off-line" : "ri:eye-line"
                      }
                    />
                  </span>
                </div>
                {errors.confirm_password && (
                  <span className="text-danger-600">
                    {errors.confirm_password.message}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={isSubmitLoading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default StaffEditPage;
